// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Modal, Radio, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import type { Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCircleCheck } from '@tabler/icons-react';
import { type JSX, useState } from 'react';
import {
  DME_CANCEL_REASON_SYSTEM,
  DME_CANCEL_REASONS,
  DME_TASK_BUSINESS_STATUS_URL,
  DME_TASK_BUSINESS_STATUSES,
} from '../../constants/dme';
import { showErrorNotification } from '../../utils/notifications';

interface FormValues {
  reason: string;
}

export interface CancelDmeOrderModalProps {
  task: Task;
  opened: boolean;
  onClose: () => void;
  onCancelled: (updatedTask: Task) => void;
}

/**
 * Reason modal for the "Cancel order" action. On confirm it cancels the Task
 * and records the chosen reason in Task.statusReason.
 */
export function CancelDmeOrderModal({ task, opened, onClose, onCancelled }: CancelDmeOrderModalProps): JSX.Element {
  const medplum = useMedplum();
  const [saving, setSaving] = useState(false);

  const form = useForm<FormValues>({
    initialValues: { reason: '' },
    validate: {
      reason: (v) => (v ? null : 'Select a cancellation reason'),
    },
  });

  const handleClose = (): void => {
    if (saving) {
      return;
    }
    form.reset();
    onClose();
  };

  const handleConfirm = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      const reason = DME_CANCEL_REASONS.find((r) => r.code === values.reason);
      if (!reason) {
        throw new Error('Unknown cancellation reason');
      }

      const cancelledTask: Task = {
        ...task,
        status: 'cancelled',
        extension: (task.extension ?? []).filter((extension) => extension.url !== DME_TASK_BUSINESS_STATUS_URL),
        statusReason: {
          coding: [{ system: DME_CANCEL_REASON_SYSTEM, code: reason.code, display: reason.label }],
          text: reason.label,
        },
      };
      const updatedTask = await medplum.updateResource<Task>(cancelledTask);

      notifications.show({
        color: 'green',
        icon: <IconCircleCheck size={16} />,
        title: 'Order cancelled',
        message: 'The DME order task has been cancelled.',
      });
      form.reset();
      onCancelled(updatedTask);
      onClose();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setSaving(false);
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title="Cancel DME order" size="lg" centered>
      <Stack gap="md">
        <Text size="sm">Select a reason for cancelling this DME order. The task will be marked as cancelled.</Text>
        <Radio.Group {...form.getInputProps('reason')}>
          <Stack gap="xs">
            {DME_CANCEL_REASONS.map((reason) => (
              <Radio key={reason.code} value={reason.code} label={reason.label} />
            ))}
          </Stack>
        </Radio.Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={saving}>
            Keep order
          </Button>
          <Button color="red" onClick={() => handleConfirm()} loading={saving}>
            Cancel order
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
